/**
 * Access level to role bundle mapping.
 * See Appendix B of the unified project plan.
 */
import { ENTRA_ROLES } from "./roles.js";
/**
 * Predefined role bundles for each access level.
 * Higher access levels include more powerful Entra roles.
 * @deprecated V6 legacy — OPEN customers now use TIER_ROLES (V7). Retained for LOCKED customer support.
 */
export const ROLE_BUNDLES = {
    FullAccess: {
        accessLevel: "FullAccess",
        description: "Full administrative access including Global Admin, Exchange, SharePoint, Teams, Intune, Security, Compliance, and User Admin",
        roleTemplateIds: [
            ENTRA_ROLES.GLOBAL_ADMIN,
            ENTRA_ROLES.EXCHANGE_ADMIN,
            ENTRA_ROLES.SHAREPOINT_ADMIN,
            ENTRA_ROLES.TEAMS_ADMIN,
            ENTRA_ROLES.INTUNE_ADMIN,
            ENTRA_ROLES.SECURITY_ADMIN,
            ENTRA_ROLES.COMPLIANCE_ADMIN,
            ENTRA_ROLES.USER_ADMIN,
        ],
    },
    StandardAccess: {
        accessLevel: "StandardAccess",
        description: "Standard administrative access including User Admin, Exchange, SharePoint, Teams, Helpdesk, License, and Authentication Admin",
        roleTemplateIds: [
            ENTRA_ROLES.USER_ADMIN,
            ENTRA_ROLES.EXCHANGE_ADMIN,
            ENTRA_ROLES.SHAREPOINT_ADMIN,
            ENTRA_ROLES.TEAMS_ADMIN,
            ENTRA_ROLES.HELPDESK_ADMIN,
            ENTRA_ROLES.LICENSE_ADMIN,
            ENTRA_ROLES.AUTHENTICATION_ADMIN,
        ],
    },
    LimitedAccess: {
        accessLevel: "LimitedAccess",
        description: "Limited access for helpdesk tasks including Helpdesk Admin, User Admin, License Admin, Service Support, and Authentication Admin",
        roleTemplateIds: [
            ENTRA_ROLES.HELPDESK_ADMIN,
            ENTRA_ROLES.USER_ADMIN,
            ENTRA_ROLES.LICENSE_ADMIN,
            ENTRA_ROLES.SERVICE_SUPPORT_ADMIN,
            ENTRA_ROLES.AUTHENTICATION_ADMIN,
        ],
    },
    ReadOnly: {
        accessLevel: "ReadOnly",
        description: "Read-only access including Global Reader, Security Reader, and Reports Reader",
        roleTemplateIds: [
            ENTRA_ROLES.GLOBAL_READER,
            ENTRA_ROLES.SECURITY_READER,
            ENTRA_ROLES.REPORTS_READER,
        ],
    },
};
/**
 * LOCKED customer Operations group roles — all 14 Entra roles except Global Administrator.
 * Used for LOCKED customer Operations groups where technicians can perform privileged
 * operations but cannot manage other admin accounts.
 */
export const LOCKED_OPERATIONS_ROLES = [
    ENTRA_ROLES.EXCHANGE_ADMIN,
    ENTRA_ROLES.SHAREPOINT_ADMIN,
    ENTRA_ROLES.TEAMS_ADMIN,
    ENTRA_ROLES.INTUNE_ADMIN,
    ENTRA_ROLES.SECURITY_ADMIN,
    ENTRA_ROLES.COMPLIANCE_ADMIN,
    ENTRA_ROLES.USER_ADMIN,
    ENTRA_ROLES.HELPDESK_ADMIN,
    ENTRA_ROLES.LICENSE_ADMIN,
    ENTRA_ROLES.SERVICE_SUPPORT_ADMIN,
    ENTRA_ROLES.AUTHENTICATION_ADMIN,
    ENTRA_ROLES.GLOBAL_READER,
    ENTRA_ROLES.SECURITY_READER,
    ENTRA_ROLES.REPORTS_READER,
];
/**
 * Access level matrix: TechnicianTier x ServiceTier -> AccessLevel.
 * Determines which role bundle each tier group receives for a given service tier.
 * @deprecated V6 legacy — V7 uses fixed TIER_ROLES per tier, independent of service tier. Retained for LOCKED customer support.
 */
export const ACCESS_LEVEL_MATRIX = {
    "Tier-0": { Enterprise: "FullAccess", Premium: "FullAccess", Standard: "FullAccess", Essential: "FullAccess", Monitoring: "ReadOnly" },
    "Tier-1": { Enterprise: "FullAccess", Premium: "FullAccess", Standard: "FullAccess", Essential: "FullAccess", Monitoring: "ReadOnly" },
    "Tier-2": { Enterprise: "StandardAccess", Premium: "StandardAccess", Standard: "StandardAccess", Essential: "LimitedAccess", Monitoring: "ReadOnly" },
    "Tier-3": { Enterprise: "LimitedAccess", Premium: "LimitedAccess", Standard: "LimitedAccess", Essential: "LimitedAccess", Monitoring: "ReadOnly" },
    "Tier-4": { Enterprise: "ReadOnly", Premium: "ReadOnly", Standard: "ReadOnly", Essential: "ReadOnly", Monitoring: "ReadOnly" },
};
/**
 * Ordered hierarchy of access levels from most to least privileged.
 */
export const ACCESS_LEVEL_HIERARCHY = [
    "FullAccess",
    "StandardAccess",
    "LimitedAccess",
    "ReadOnly",
];
/**
 * Numeric rank for access levels. Lower number = more privileged.
 */
export const ACCESS_LEVEL_RANK = {
    FullAccess: 0,
    StandardAccess: 1,
    LimitedAccess: 2,
    ReadOnly: 3,
};
/**
 * V7 fixed-roles-per-tier model.
 * Each tier has a fixed set of Entra roles regardless of customer service tier.
 * Used for OPEN customers. LOCKED customers continue using Privileged/Operations model.
 */
export const TIER_ROLES = {
    "Tier-0": {
        description: "Break-Glass — Global Administrator only (members also hold Tier-1 access via elevation)",
        roleTemplateIds: [
            ENTRA_ROLES.GLOBAL_ADMIN,
        ],
    },
    "Tier-1": {
        description: "Senior Tech — 25 roles (15 CIPP + 10 additional)",
        roleTemplateIds: [
            // CIPP 15
            ENTRA_ROLES.APPLICATION_ADMIN,
            ENTRA_ROLES.AUTH_POLICY_ADMIN,
            ENTRA_ROLES.BILLING_ADMIN,
            ENTRA_ROLES.CLOUD_APP_SECURITY_ADMIN,
            ENTRA_ROLES.CLOUD_DEVICE_ADMIN,
            ENTRA_ROLES.DOMAIN_NAME_ADMIN,
            ENTRA_ROLES.EXCHANGE_ADMIN,
            ENTRA_ROLES.GLOBAL_READER,
            ENTRA_ROLES.INTUNE_ADMIN,
            ENTRA_ROLES.PRIVILEGED_AUTH_ADMIN,
            ENTRA_ROLES.PRIVILEGED_ROLE_ADMIN,
            ENTRA_ROLES.SECURITY_ADMIN,
            ENTRA_ROLES.SHAREPOINT_ADMIN,
            ENTRA_ROLES.TEAMS_ADMIN,
            ENTRA_ROLES.USER_ADMIN,
            // 10 additional
            ENTRA_ROLES.COMPLIANCE_ADMIN,
            ENTRA_ROLES.CONDITIONAL_ACCESS_ADMIN,
            ENTRA_ROLES.IDENTITY_GOVERNANCE_ADMIN,
            ENTRA_ROLES.HYBRID_IDENTITY_ADMIN,
            ENTRA_ROLES.WINDOWS_365_ADMIN,
            ENTRA_ROLES.POWER_PLATFORM_ADMIN,
            ENTRA_ROLES.ATTRIBUTE_DEFINITION_ADMIN,
            ENTRA_ROLES.FABRIC_ADMIN,
            ENTRA_ROLES.DYNAMICS_365_ADMIN,
            ENTRA_ROLES.SERVICE_SUPPORT_ADMIN,
        ],
    },
    "Tier-2": {
        description: "Standard Tech — 14 roles (6 CIPP + 8 additional)",
        roleTemplateIds: [
            // CIPP 6
            ENTRA_ROLES.CLOUD_DEVICE_ADMIN,
            ENTRA_ROLES.EXCHANGE_ADMIN,
            ENTRA_ROLES.GLOBAL_READER,
            ENTRA_ROLES.INTUNE_ADMIN,
            ENTRA_ROLES.SHAREPOINT_ADMIN,
            ENTRA_ROLES.USER_ADMIN,
            // 8 additional
            ENTRA_ROLES.TEAMS_ADMIN,
            ENTRA_ROLES.AUTHENTICATION_ADMIN,
            ENTRA_ROLES.PRINTER_ADMIN,
            ENTRA_ROLES.OFFICE_APPS_ADMIN,
            ENTRA_ROLES.DESKTOP_ANALYTICS_ADMIN,
            ENTRA_ROLES.SECURITY_READER,
            ENTRA_ROLES.SERVICE_SUPPORT_ADMIN,
            ENTRA_ROLES.MESSAGE_CENTER_READER,
        ],
    },
    "Tier-3": {
        description: "Junior Tech — 10 roles (3 CIPP + 7 additional)",
        roleTemplateIds: [
            // CIPP 3
            ENTRA_ROLES.EXCHANGE_ADMIN,
            ENTRA_ROLES.GLOBAL_READER,
            ENTRA_ROLES.USER_ADMIN,
            // 7 additional
            ENTRA_ROLES.TEAMS_COMMS_SUPPORT_ENGINEER,
            ENTRA_ROLES.TEAMS_DEVICES_ADMIN,
            ENTRA_ROLES.TEAMS_TELEPHONY_ADMIN,
            ENTRA_ROLES.AUTHENTICATION_ADMIN,
            ENTRA_ROLES.SECURITY_READER,
            ENTRA_ROLES.PRINTER_TECHNICIAN,
            ENTRA_ROLES.MESSAGE_CENTER_READER,
        ],
    },
    "Tier-4": {
        description: "Read-Only — 4 reader roles",
        roleTemplateIds: [
            ENTRA_ROLES.GLOBAL_READER,
            ENTRA_ROLES.SECURITY_READER,
            ENTRA_ROLES.REPORTS_READER,
            ENTRA_ROLES.MESSAGE_CENTER_READER,
        ],
    },
};
/**
 * Default roles for Teams Specialist access.
 * Configurable via Settings — these are the initial defaults.
 */
export const TEAMS_SPECIALIST_DEFAULT_ROLES = [
    ENTRA_ROLES.USER_ADMIN,
    ENTRA_ROLES.TEAMS_ADMIN,
    ENTRA_ROLES.EXCHANGE_ADMIN,
];
//# sourceMappingURL=access-levels.js.map